/**
 * EverwebinarConfigPanel
 * Panel konfiguracji integracji z EverWebinar.
 * Wyświetlany inline w karcie integracji.
 *
 * Umożliwia:
 *  - wpisanie API Key + wybór webinaru z listy
 *  - ustawienie minuty, od której liczymy "dotarł do oferty"
 *  - ręczny sync (data opcjonalna, domyślnie wczoraj)
 *  - rozłączenie integracji
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Loader2, Check, RefreshCw, Trash2, Info } from "lucide-react";

interface EverwebinarStatus {
  connected: boolean;
  webinar_id?: number;
  webinar_name?: string;
  offer_start_seconds?: number;
  offer_start_minutes?: number;
  enabled?: boolean;
  last_synced_at?: string;
  last_synced_date?: string;
}

interface Webinar {
  id: number;
  name: string;
  status?: string;
}

export default function EverwebinarConfigPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  // ─── Form state ───────────────────────────────────────────
  const [apiKey, setApiKey] = useState("");
  const [webinarId, setWebinarId] = useState<number | "">("");
  const [offerMinute, setOfferMinute] = useState<number | "">(45);
  const [webinarsList, setWebinarsList] = useState<Webinar[]>([]);
  const [loadingWebinars, setLoadingWebinars] = useState(false);
  const [webinarsError, setWebinarsError] = useState("");
  const [syncDate, setSyncDate] = useState("");
  const [feedback, setFeedback] = useState<{ type: "ok" | "err"; msg: string } | null>(null);

  // ─── Status query ─────────────────────────────────────────
  const { data: status, isLoading: statusLoading } = useQuery<EverwebinarStatus>({
    queryKey: ["ew-status"],
    queryFn: () => apiRequest("GET", "/api/integrations/everwebinar/status"),
    staleTime: 30_000,
  });

  const connected = status?.connected ?? false;

  // ─── Connect mutation ─────────────────────────────────────
  const connectMut = useMutation({
    mutationFn: () =>
      apiRequest<{ webinar_name?: string | undefined; webinar_id?: string | number | undefined }>(
        "POST",
        "/api/integrations/everwebinar/connect",
        {
          api_key: apiKey,
          webinar_id: Number(webinarId),
          offer_start_seconds: Number(offerMinute) * 60,
        }
      ),
    onSuccess: (data: { webinar_name?: string; webinar_id?: string | number }) => {
      setFeedback({
        type: "ok",
        msg: t("everwebinar.feedback.connected", {
          name: data.webinar_name ?? `Webinar #${data.webinar_id}`,
        }),
      });
      qc.invalidateQueries({ queryKey: ["ew-status"] });
      qc.invalidateQueries({ queryKey: ["hub-ew"] });
    },
    onError: (err) => {
      setFeedback({ type: "err", msg: err?.message ?? t("everwebinar.feedback.connectError") });
    },
  });

  // ─── Disconnect mutation ──────────────────────────────────
  const disconnectMut = useMutation({
    mutationFn: () => apiRequest("POST", "/api/integrations/everwebinar/disconnect"),
    onSuccess: () => {
      setFeedback({ type: "ok", msg: t("everwebinar.feedback.disconnected") });
      setApiKey("");
      setWebinarId("");
      setWebinarsList([]);
      qc.invalidateQueries({ queryKey: ["ew-status"] });
      qc.invalidateQueries({ queryKey: ["hub-ew"] });
    },
    onError: (err) => {
      setFeedback({ type: "err", msg: err?.message ?? t("everwebinar.feedback.disconnectError") });
    },
  });

  // ─── Sync mutation ────────────────────────────────────────
  const syncMut = useMutation({
    mutationFn: () =>
      apiRequest<{
        date?: string | undefined;
        synced?: number | undefined;
        schedules?: number | undefined;
      }>("POST", "/api/integrations/everwebinar/sync", {
        ...(syncDate ? { date: syncDate } : {}),
      }),
    onSuccess: (data: { date?: string; synced?: number; schedules?: number }) => {
      setFeedback({
        type: "ok",
        msg: t("everwebinar.feedback.synced", {
          date: data.date,
          count: data.synced ?? 0,
          schedules: data.schedules ?? 0,
        }),
      });
      qc.invalidateQueries({ queryKey: ["ew-status"] });
    },
    onError: (err) => {
      setFeedback({ type: "err", msg: err?.message ?? t("everwebinar.feedback.syncError") });
    },
  });

  // ─── Fetch webinar list by API key ────────────────────────
  async function fetchWebinars() {
    if (!apiKey.trim()) {
      setWebinarsError(t("everwebinar.errors.apiKeyRequired"));
      return;
    }
    setLoadingWebinars(true);
    setWebinarsError("");
    try {
      const data: { webinars?: Webinar[] } = await apiRequest(
        "GET",
        `/api/integrations/everwebinar/webinars?api_key=${encodeURIComponent(apiKey)}`
      );
      setWebinarsList(data.webinars ?? []);
      if ((data.webinars ?? []).length === 0) setWebinarsError(t("everwebinar.errors.noWebinars"));
    } catch (e) {
      setWebinarsError((e as Error)?.message ?? t("everwebinar.errors.fetchList"));
    } finally {
      setLoadingWebinars(false);
    }
  }

  if (statusLoading) {
    return (
      <div className="flex items-center gap-2 text-xs text-white/40 py-2">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> {t("everwebinar.loading")}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* ── Feedback ── */}
      {feedback && (
        <div
          className={`flex items-start gap-2 p-3 rounded-lg text-xs border ${
            feedback.type === "ok"
              ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-300"
              : "bg-red-500/10 border-red-500/20 text-red-300"
          }`}
        >
          {feedback.type === "ok" ? (
            <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          ) : (
            <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          )}
          <span>{feedback.msg}</span>
        </div>
      )}

      {/* ── Connected state ── */}
      {connected && status && (
        <div className="p-3 bg-emerald-500/[0.06] border border-emerald-500/20 rounded-lg space-y-1.5">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse flex-shrink-0" />
            <span className="text-xs font-medium text-emerald-300">
              {status.webinar_name ?? `Webinar #${status.webinar_id}`}
            </span>
          </div>
          <div className="text-xs text-white/50 space-y-0.5 pl-4">
            <div>
              {t("everwebinar.connected.offerMinute")}:{" "}
              <span className="text-white/70">
                {t("everwebinar.connected.minutes", {
                  count:
                    status.offer_start_minutes ??
                    Math.round((status.offer_start_seconds ?? 2700) / 60),
                })}
              </span>
            </div>
            {status.last_synced_date && (
              <div>
                {t("everwebinar.connected.lastSync")}:{" "}
                <span className="text-white/70">{status.last_synced_date}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Config form (only when not connected) ── */}
      {!connected && (
        <div className="space-y-3">
          {/* API Key */}
          <div>
            <label className="text-[11px] text-white/50 block mb-1">
              {t("everwebinar.form.apiKey")}
            </label>
            <input
              type="text"
              value={apiKey}
              onChange={(e) => {
                setApiKey(e.target.value);
                setWebinarsList([]);
              }}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              className="w-full bg-white/[0.04] border border-white/[0.10] rounded-lg px-3 py-2 text-xs text-white placeholder-white/25 focus:outline-none focus:border-white/25 font-mono"
            />
            <p className="text-[10px] text-white/30 mt-1">{t("everwebinar.form.apiKeyHint")}</p>
          </div>

          {/* Load webinars button */}
          <button
            onClick={fetchWebinars}
            disabled={!apiKey.trim() || loadingWebinars}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-white/[0.10] rounded-lg text-white/60 hover:text-white/90 hover:border-white/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {loadingWebinars ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <RefreshCw className="w-3 h-3" />
            )}
            {t("everwebinar.form.fetchList")}
          </button>
          {webinarsError && <p className="text-[11px] text-red-400">{webinarsError}</p>}

          {/* Webinar select */}
          {webinarsList.length > 0 && (
            <div>
              <label className="text-[11px] text-white/50 block mb-1">
                {t("everwebinar.form.webinar")}
              </label>
              <select
                value={webinarId}
                onChange={(e) => setWebinarId(Number(e.target.value))}
                className="w-full bg-white/[0.04] border border-white/[0.10] rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-white/25"
              >
                <option value="">{t("everwebinar.form.selectWebinar")}</option>
                {webinarsList.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name} (ID: {w.id})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Manual webinar ID (if API didn't return list) */}
          {webinarsList.length === 0 && (
            <div>
              <label className="text-[11px] text-white/50 block mb-1">
                {t("everwebinar.form.webinarId")}{" "}
                <span className="text-white/30">{t("everwebinar.form.webinarIdHint")}</span>
              </label>
              <input
                type="number"
                value={webinarId}
                onChange={(e) => setWebinarId(e.target.value ? Number(e.target.value) : "")}
                placeholder={t("everwebinar.form.webinarIdPlaceholder")}
                className="w-full bg-white/[0.04] border border-white/[0.10] rounded-lg px-3 py-2 text-xs text-white placeholder-white/25 focus:outline-none focus:border-white/25"
              />
            </div>
          )}

          {/* Offer minute */}
          <div>
            <label className="text-[11px] text-white/50 block mb-1">
              {t("everwebinar.form.offerMinute")}
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={offerMinute}
                onChange={(e) => setOfferMinute(e.target.value ? Number(e.target.value) : "")}
                min={1}
                max={300}
                className="w-24 bg-white/[0.04] border border-white/[0.10] rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-white/25"
              />
              <span className="text-xs text-white/40">{t("everwebinar.form.minShort")}</span>
            </div>
            <p className="text-[10px] text-white/30 mt-1">
              {t("everwebinar.form.offerMinuteHint")}
            </p>
          </div>

          {/* Connect button */}
          <button
            onClick={() => connectMut.mutate()}
            disabled={!apiKey.trim() || !webinarId || !offerMinute || connectMut.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-medium rounded-lg transition-colors"
          >
            {connectMut.isPending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Check className="w-3.5 h-3.5" />
            )}
            {t("everwebinar.form.connect")}
          </button>
        </div>
      )}

      {/* ── Actions when connected ── */}
      {connected && (
        <div className="space-y-3">
          {/* Manual sync */}
          <div>
            <label className="text-[11px] text-white/50 block mb-1">
              {t("everwebinar.sync.label")}{" "}
              <span className="text-white/30">{t("everwebinar.sync.hint")}</span>
            </label>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={syncDate}
                onChange={(e) => setSyncDate(e.target.value)}
                className="bg-white/[0.04] border border-white/[0.10] rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-white/25"
              />
              <button
                onClick={() => syncMut.mutate()}
                disabled={syncMut.isPending}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-white/[0.06] hover:bg-white/[0.10] border border-white/[0.10] text-xs text-white/70 rounded-lg transition-colors disabled:opacity-40"
              >
                {syncMut.isPending ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <RefreshCw className="w-3 h-3" />
                )}
                {t("everwebinar.sync.button")}
              </button>
            </div>
          </div>

          {/* Cron info */}
          <div className="p-3 bg-white/[0.02] border border-white/[0.06] rounded-lg space-y-2">
            <p className="text-[11px] text-white/40 leading-relaxed">
              {t("everwebinar.cron.text")}
            </p>
          </div>

          {/* Disconnect */}
          <button
            onClick={() => disconnectMut.mutate()}
            disabled={disconnectMut.isPending}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-red-500/20 rounded-lg text-red-400/70 hover:text-red-400 hover:border-red-500/40 disabled:opacity-40 transition-colors"
          >
            {disconnectMut.isPending ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Trash2 className="w-3 h-3" />
            )}
            {t("everwebinar.disconnect")}
          </button>
        </div>
      )}
    </div>
  );
}
